import { __decorate } from "tslib";
import { Component, } from 'react';
import { findDOMNode } from 'react-dom';
import classNames from 'classnames';
import { action, computed, observable } from 'mobx';
import omit from 'lodash/omit';
import defer from 'lodash/defer';
import noop from 'lodash/noop';
import classes from 'component-classes';
import ConfigContext from '../../../es/config-provider/ConfigContext';
import autobind from '../_util/autobind';
import { Size } from './enum';
import normalizeLanguage from '../_util/normalizeLanguage';
import localeContext from '../locale-context';
/**
 * Code生成器
 */
const CodeGen = (function* (start) {
    while (true) {
        yield `anonymous-${++start}`;
    }
})(1000);
/* eslint-disable react/no-unused-prop-types */
export default class ViewComponent extends Component {
    constructor(props, context) {
        super(props, context);
        this.setCode(props);
        this.setObservableProps(props, context);
        this.prefixCls = this.getContextProPrefixCls(props.suffixCls, props.prefixCls);
    }
    static get contextType() {
        return ConfigContext;
    }
    get lang() {
        const { lang } = this.observableProps;
        if (lang) {
            return lang;
        }
        return localeContext.locale.lang;
    }
    get disabled() {
        return this.isDisabled();
    }
    setCode(props) {
        this.code = props.code || CodeGen.next().value;
    }
    getMergedClassNames(...props) {
        return classNames(this.getClassName(), this.getWrapperClassNames(), ...props);
    }
    getMergedProps(props = {}) {
        const wrapperProps = this.getWrapperProps(props);
        const otherProps = this.getOtherProps();
        const mergedProps = {
            ...wrapperProps,
            ...otherProps,
            className: classNames(wrapperProps.className, otherProps.className),
        };
        if (wrapperProps.style && otherProps.style) {
            mergedProps.style = { ...wrapperProps.style, ...otherProps.style };
        }
        return mergedProps;
    }
    getObservableProps(props, _context) {
        return {
            lang: props.lang,
            disabled: props.disabled,
        };
    }
    setObservableProps(props, context) {
        this.observableProps = this.getObservableProps(props, context);
    }
    updateObservableProps(props, context) {
        Object.assign(this.observableProps, this.getObservableProps(props, context));
    }
    getOmitPropsKeys() {
        const keys = [
            'code',
            'prefixCls',
            'suffixCls',
            'className',
            'elementClassName',
            'style',
            'size',
            'autoFocus',
            'onFocus',
            'onBlur',
            'children',
            'dataSet',
        ];
        if (this.disabled) {
            return keys.concat([
                'onClick',
                'onMouseUp',
                'onMouseDown',
                'onMouseEnter',
                'onMouseLeave',
                // 'onMouseOver',
                // 'onMouseOut',
                'onKeyDown',
                'onKeyUp',
                'onKeyPress',
                'onContextMenu',
            ]);
        }
        return keys;
    }
    getOtherProps() {
        const { props, disabled } = this;
        const { tabIndex, lang, style = {} } = props;
        const otherProps = omit(props, this.getOmitPropsKeys());
        if (disabled) {
            if (tabIndex !== undefined) {
                otherProps.tabIndex = -1;
            }
        }
        else {
            otherProps.onFocus = this.handleFocus;
            otherProps.onBlur = this.handleBlur;
        }
        otherProps.ref = this.elementReference;
        otherProps.disabled = disabled;
        otherProps.className = this.getClassName();
        otherProps.style = {};
        if (this.height) {
            otherProps.style.height = this.height;
        }
        else if ('height' in style) {
            otherProps.style.height = style.height;
        }
        if ('minHeight' in style) {
            otherProps.style.minHeight = style.minHeight;
        }
        if ('maxHeight' in style) {
            otherProps.style.maxHeight = style.maxHeight;
        }
        otherProps.lang = normalizeLanguage(lang);
        return otherProps;
    }
    getClassName(...props) {
        const { prefixCls, props: { elementClassName }, } = this;
        return classNames(prefixCls, elementClassName, ...props);
    }
    getWrapperProps(props = {}) {
        const { style, hidden, onMouseEnter, onMouseLeave } = this.props;
        const mergedStyle = props.style || style;
        const wrapperProps = {
            ref: this.wrapperReference,
            className: this.getWrapperClassNames(),
            hidden,
            ...props,
        };
        if (this.disabled) {
            if (!wrapperProps.onMouseEnter) {
                wrapperProps.onMouseEnter = onMouseEnter;
            }
            if (!wrapperProps.onMouseLeave) {
                wrapperProps.onMouseLeave = onMouseLeave;
            }
        }
        if (mergedStyle) {
            wrapperProps.style = omit(mergedStyle, ['height', 'minHeight', 'maxHeight']);
        }
        return wrapperProps;
    }
    getWrapperClassNames(...args) {
        const { prefixCls, props: { className, size }, } = this;
        return classNames(`${prefixCls}-wrapper`, className, {
            [`${prefixCls}-sm`]: size === Size.small,
            [`${prefixCls}-lg`]: size === Size.large,
            [`${prefixCls}-disabled`]: this.disabled,
            [`${prefixCls}-focused`]: this.useFocusedClassName() && this.isFocus,
        }, ...args);
    }
    isDisabled() {
        const { disabled = false } = this.observableProps;
        return disabled;
    }
    useFocusedClassName() {
        return true;
    }
    handleFocus(e) {
        this.isFocused = true;
        this.isFocus = true;
        const { props: { onFocus = noop }, prefixCls, } = this;
        if (this.useFocusedClassName()) {
            const element = this.wrapper || findDOMNode(this);
            if (element) {
                classes(element).add(`${prefixCls}-focused`);
            }
        }
        onFocus(e);
    }
    forceBlur(e) {
        const { props: { onBlur = noop }, } = this;
        onBlur(e);
    }
    handleBlur(e) {
        if (!e.isDefaultPrevented()) {
            const { prefixCls } = this;
            this.forceBlur(e);
            if (!e.isDefaultPrevented()) {
                this.isFocused = false;
                this.isFocus = false;
                if (this.useFocusedClassName()) {
                    const element = this.wrapper || findDOMNode(this);
                    if (element) {
                        classes(element).remove(`${prefixCls}-focused`);
                    }
                }
            }
        }
    }
    focus() {
        if (this.element) {
            this.element.focus();
        }
    }
    blur() {
        if (this.element) {
            this.element.blur();
        }
    }
    elementReference(node) {
        this.element = node;
    }
    wrapperReference(node) {
        this.wrapper = node;
    }
    componentWillReceiveProps(nextProps, nextContext) {
        const { code, prefixCls } = this.props;
        const { prefixCls: nextPrefixCls } = nextProps;
        if (prefixCls !== nextPrefixCls) {
            this.prefixCls = this.getContextProPrefixCls(nextProps.suffixCls, nextPrefixCls);
        }
        if (nextProps.code !== code) {
            this.setCode(nextProps);
        }
        this.updateObservableProps(nextProps, nextContext);
    }
    componentDidMount() {
        const { tabIndex, autoFocus } = this.props;
        if (autoFocus && (tabIndex === undefined || tabIndex > -1) && !this.disabled) {
            defer(() => this.focus());
        }
    }
    setHeight(height) {
        this.height = height;
        const { element } = this;
        if (element) {
            element.style.height = height;
        }
    }
    getContextConfig(key) {
        const { context } = this;
        return context.getConfig(key);
    }
    getContextProPrefixCls(suffixCls, customizePrefixCls) {
        const { context } = this;
        return context.getProPrefixCls(suffixCls, customizePrefixCls);
    }
}
__decorate([
    observable
], ViewComponent.prototype, "isFocused", void 0);
__decorate([
    observable
], ViewComponent.prototype, "observableProps", void 0);
__decorate([
    computed
], ViewComponent.prototype, "lang", null);
__decorate([
    computed
], ViewComponent.prototype, "disabled", null);
__decorate([
    action
], ViewComponent.prototype, "setObservableProps", null);
__decorate([
    action
], ViewComponent.prototype, "updateObservableProps", null);
__decorate([
    autobind,
    action
], ViewComponent.prototype, "handleFocus", null);
__decorate([
    autobind,
    action
], ViewComponent.prototype, "handleBlur", null);
__decorate([
    autobind
], ViewComponent.prototype, "elementReference", null);
__decorate([
    autobind,
    action
], ViewComponent.prototype, "wrapperReference", null);
__decorate([
    autobind
], ViewComponent.prototype, "getContextConfig", null);
__decorate([
    autobind
], ViewComponent.prototype, "getContextProPrefixCls", null);
